import React, { useEffect, useContext, useState } from 'react';
import Page from '../../../components/Page';
import Panel from '../../../components/Panel';
import RoundedButton from '../../../components/RoundedButton';
import AuthContext from '../../../contexts/AuthContext';
import { ProgressStep, ProgressStepsContainer } from '../../../components/Dashboard/ProgressSteps';
import useAPI from '../../../hooks/useAPI';
import moment from 'moment/moment';
import PanelTag from '../../../components/Panel/PanelTag';
import PanelTable from '../../../components/Panel/PanelTable';
import { ReactComponent as Checked } from '../../../icons/checked.svg';
import { ReactComponent as Unchecked } from '../../../icons/unchecked.svg';
import PlaidButton from '../../../components/PlaidButton';
import Button from '../../../components/Button';

export default function SystemRegistration ({ history }) {
    const { system, isComingSoonFeatureEnabled } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [workflow, setWorkflow] = useState(null);
    const [registrationMessage, setRegistrationMessage] = useState('...');
    const [completedStepCount, setCompletedStepCount] = useState(0);
    const [steps, setSteps] = useState([]);
    const [progressReport, setProgressReport] = useState([]);
    const [lastUpdatedAt, setLastUpdatedAt] = useState('');
    const [paymentSummary, setPaymentSummary] = useState({});
    const [showEnphaseLinkButton, setShowEnphaseLinkButton] = useState(system.resource.show_enphase_link_button);
    const api = useAPI();

    const handleEnphaseConnect = async () => {
        setLoading(true);
        api.json('GET', `systems/${system.id}/enphase/auth-url`)
            .then((r)=>r.json())
            .then((response)=>{
                if (response.url) {
                    window.location.href = response.url; // Redirect user to Enphase login
                    return;
                }

                alert(response.error || 'Unable to get Enphase login URL.');
            })
            .catch((err) => {
                alert('Error fetching enphase auth URL: ' + err);
            })
            .then(()=> setLoading(false))
    };

    const loadDashboard = ()=>{
        setLoading(true);

        api.json('GET', '/systems/' + system.id + '/dashboard')
            .then((r)=>r.json())
            .then((response)=>{
                if(!response.data){
                    return;
                }

                const steps = response?.data?.registration_progress?.steps || [];
                let completeCount = 0;
    
                steps.forEach(step => {
                    if (step.completed) {
                        completeCount++;
                    }
                });

                setSteps(steps);
                setCompletedStepCount(completeCount);
                setProgressReport(response.data?.progress_report || []);
                setLastUpdatedAt(response.data?.registration_progress?.last_updated_at || '');
                setPaymentSummary(response.data?.payment_summary || {});
                setRegistrationMessage(response.data?.registration_progress?.message || '');
                setWorkflow(response.data?.workflow || null);
                api.updateSystemData(response.data.system)
                setShowEnphaseLinkButton(response.data.system.resource.show_enphase_link_button)
            })
            .catch(() => {
                alert('Unable to load dashboard.');
            })
            .then(()=>setLoading(false))
    }

    useEffect(()=>{
        loadDashboard();
        // eslint-disable-next-line
    }, []);

    let paymentMessage = '';
    if (!paymentSummary.payments_to_date) {
        paymentMessage = 'No payments have been issued.';
    }

    let timelineURL = '';
    if (workflow && workflow.handle) {
        if (workflow.handle.includes('virginia')) {
            timelineURL = 'https://srechelp.carbonsolutionsgroup.com/hc/en-us/articles/8481147925268-Virginia-SREC-Workflow-';
        } else if (workflow.handle.includes('pennsylvania')) {
            timelineURL = 'https://srechelp.carbonsolutionsgroup.com/hc/en-us/articles/8481418271636-Pennsylvania-SREC-Workflow-';
        } else if (workflow.handle.includes('maryland')) {
            timelineURL = 'https://srechelp.carbonsolutionsgroup.com/hc/en-us/articles/8481519041812-Maryland-SREC-Workflow';
        } else if (workflow.handle.includes('newjersey_susi')) {
            timelineURL = 'https://srechelp.carbonsolutionsgroup.com/hc/en-us/articles/13350731428756';
        }
    }

    return (
        <Page title="System Registration" padded="true" busy={loading} minWidth={800}>
            <div className='row'>
                <div className='col-12 col-md-8'>
                    {
                        !isComingSoonFeatureEnabled
                            && <>
                                <Panel
                                    title="System Registration Process"
                                    description={registrationMessage}
                                    primary="true"
                                    panelStyle={{ minWidth: 400 }}
                                    detailStyle={{ marginTop: 5, alignSelf: 'flex-start' }}
                                    details={<div><span style={{ fontWeight: 600 }}>{completedStepCount}</span> of <span
                                        style={{ fontWeight: 600 }}>{steps.length} steps</span> completed</div>}
                                >
                                    <ProgressStepsContainer>
                                        {steps.map((step, k) => {
                                            return <ProgressStep key={k} step={step}/>
                                        })}
                                    </ProgressStepsContainer>
                                    {
                                        timelineURL ?
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                marginTop: 10,
                                                paddingTop: 15,
                                                borderTop: 'solid #eee 1px'
                                            }}>
                                                <RoundedButton
                                                    onClick={() => {
                                                        window.open(timelineURL, '_blank');
                                                    }}
                                                >Timeline Details</RoundedButton>
                                            </div>
                                            : null
                                    }
                                </Panel>
                            </>
                    }
                    <Panel
                        title="Progress Report"
                        description="System registration details and progress status"
                        primary="true"
                        contentStyle={{ padding: 0 }}
                        details={
                            lastUpdatedAt ? (
                                <PanelTag>Updated {moment(lastUpdatedAt).format('MMM D, YYYY')}</PanelTag>
                            ) : null
                        }
                    >
                        <PanelTable
                            headers={['Task Description', 'Date']}
                            items={progressReport}
                            render={(item) => {
                                return (
                                    <>
                                        <td>
                                <span style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 20,
                                    alignItems: 'center'
                                }}>
                                    <span>{item.completed ? <Checked/> : <Unchecked/>}</span>
                                    <span>{item.labels.join(' ')}</span>
                                </span>
                                        </td>
                                        <td>
                                <span style={{
                                    fontWeight: 500,
                                    fontSize: '0.95rem',
                                    whiteSpace: 'nowrap'
                                }}>{item.date ? moment(item.date).format('MMM DD, YYYY') : '-'}</span>
                                        </td>
                                    </>
                                )
                            }}
                        />
                    </Panel>
                </div>
                <div className='col-12 col-md-4'>
                    <div className='row'>
                        <div className='col-12'>
                            <Panel
                                title="System Overview"
                            >
                                Once system registration is completed, system data will display.
                            </Panel>
                        </div>
                    </div>
                    {
                        system.is_owner && showEnphaseLinkButton ?
                            <div className='row'>
                                <div className='col-12'>
                                    <Panel
                                        title="⚠️ Action Required"
                                        primary="true"
                                        panelStyle={{
                                            border: '2px solid #f59e0b',
                                            backgroundColor: '#fffbeb'
                                        }}
                                        footer={
                                            <div style={{
                                                marginTop: 10,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: 4
                                            }}>
                                                <RoundedButton
                                                    onClick={handleEnphaseConnect}
                                                    disabled={loading}
                                                    style={{
                                                        backgroundColor: '#f59e0b',
                                                        borderColor: '#f59e0b',
                                                        color: 'white',
                                                        fontSize: '16px',
                                                        padding: '8px 20px'
                                                    }}
                                                >
                                                    Link Your Monitoring
                                                </RoundedButton>
                                            </div>
                                        }
                                    >
                                        Please link your monitoring to ensure access for REC generation.
                                    </Panel>
                                </div>
                            </div>
                            : null
                    }
                    {
                        system.is_owner ?
                            <div className='row'>
                                <div className='col-12'>
                                    <Panel
                                        title="Payment Details"
                                        footer={

                                            <div style={{
                                                marginTop: 10,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: 4
                                            }}>
                                                {
                                                    paymentSummary && paymentSummary.payment_method_count === 0 ?
                                                        <>
                                                            <PlaidButton/>
                                                            <Button
                                                                onClick={() => history.push('/account/payment-settings')}
                                                                style={{padding: 0}}>
                                                                Payment Settings
                                                            </Button>
                                                        </> :
                                                        <RoundedButton
                                                            onClick={() => history.push('/account/payment-settings')}>
                                                            Payment Settings
                                                        </RoundedButton>
                                                }
                                            </div>

                                        }
                                    >
                                        {paymentMessage}

                                    </Panel>
                                </div>
                            </div>
                            : null
                    }
                    <div className='row'>
                        <div className='col-12'>
                            <Panel
                                title="Account Settings"
                                footer={
                                    <div style={{
                                        marginTop: 10,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 4
                                    }}>
                                        <RoundedButton solid={true} onClick={() => history.push('/account')}>
                                            My Account
                                        </RoundedButton>
                                    </div>
                                }
                            >
                                Update profile settings, change password, or add an additional user.
                            </Panel>
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};